import React, { useState, useEffect } from 'react';

const QuestionnaireForm = ({ questionnaire, responses, onResponsesChange, errors = {} }) => {
  const [localResponses, setLocalResponses] = useState({});

  useEffect(() => {
    if (responses && responses.responses) {
      // Convert API format back to local format
      const localFormat = {};
      responses.responses.forEach(response => {
        localFormat[response.questionId] = response.answer;
      });
      setLocalResponses(localFormat);
    } else {
      setLocalResponses({});
    }
  }, [responses]);

  const handleResponseChange = (questionId, answer) => {
    const updatedResponses = {
      ...localResponses,
      [questionId]: answer
    };
    setLocalResponses(updatedResponses);
    
    // Convert to the format expected by the API
    const formattedResponses = {
      responses: Object.keys(updatedResponses).map(qId => ({
        questionId: qId,
        answer: updatedResponses[qId]
      }))
    };
    
    onResponsesChange(formattedResponses);
  };

  const handleMultipleChoiceChange = (questionId, option, isChecked, allowMultiple) => {
    if (allowMultiple) {
      // Multiple selection (checkboxes)
      const currentAnswer = localResponses[questionId] || [];
      let newAnswer;
      
      if (isChecked) {
        newAnswer = [...currentAnswer, option];
      } else {
        newAnswer = currentAnswer.filter(opt => opt !== option);
      }
      
      handleResponseChange(questionId, newAnswer);
    } else {
      // Single selection (radio buttons)
      handleResponseChange(questionId, option);
    }
  };

  if (!questionnaire || !questionnaire.questions || questionnaire.questions.length === 0) {
    return null;
  }

  return (
    <div className="questionnaire-form bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      {/* Header Section */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <h4 className="text-lg font-medium text-gray-900">Additional Information</h4>
          <p className="text-sm text-gray-600">Please answer the following questions to help us serve you better.</p>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-8">
        {questionnaire.questions.map((question, index) => (
          <div key={question.id} className={`${errors[question.id] ? 'ring-2 ring-red-300 bg-red-50 rounded-lg p-4' : ''}`}>
            
            {/* Question Header */}
            <div className="flex items-start space-x-3 mb-4">
              <div className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 mt-1">
                {index + 1}
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-900 leading-relaxed">
                  {question.question}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {question.required && (
                  <p className="text-xs text-gray-500 mt-1">Required field</p>
                )}
              </div>
            </div>

            {/* Answer Section */}
            <div className="ml-9">
              {question.type === 'text' && (
                <div>
                  <input
                    type="text"
                    className={`w-full px-4 py-3 border rounded-lg shadow-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                      errors[question.id] ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                    value={localResponses[question.id] || ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    placeholder="Enter your answer..."
                  />
                  {errors[question.id] && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {errors[question.id]}
                    </p>
                  )}
                </div>
              )}

              {question.type === 'multiple_choice' && (
                <div>
                  <div className="space-y-3">
                    {(question.options || []).map((option, optionIndex) => (
                      <label 
                        key={optionIndex} 
                        className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type={question.allowMultiple ? "checkbox" : "radio"}
                          name={question.allowMultiple ? undefined : `question-${question.id}`}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          checked={question.allowMultiple 
                            ? (Array.isArray(localResponses[question.id]) ? localResponses[question.id].includes(option) : false)
                            : localResponses[question.id] === option
                          }
                          onChange={(e) => handleMultipleChoiceChange(question.id, option, e.target.checked, question.allowMultiple)}
                        />
                        <span className="text-sm font-medium text-gray-900">{option}</span>
                      </label>
                    ))}
                  </div>
                  {errors[question.id] && (
                    <p className="mt-3 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {errors[question.id]}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-600">
            Your responses help us personalize your experience and ensure we can provide the best possible service.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireForm;